import { useQuery } from '@tanstack/react-query';
import api, { apiDmars } from '../../../config/http';

export interface QueryResponse<T> {
  result: T;
  resultType: number;
}

export interface Region {
  codigo: number;
  descripcion: string;
  codigoRegion: string;
}

export interface Provincia {
  codigo: number;
  descripcion: string;
  codigoPadre: number;
}

export interface Municipio {
  codigo: number;
  descripcion: string;
  codigoPadre: number;
  codigoMunicipio: string;
}
export const useRegionesQuery = () => {
  return useQuery({
    queryKey: ['regiones'],
    queryFn: () => api.get<QueryResponse<Region[]>>('utility/getregiones').then((res) => res.data),
  });
};

export const useProvinciasQuery = () => {
  return useQuery<QueryResponse<Provincia[]>>({
    queryKey: ['provincias'],
    queryFn: () => api.get<QueryResponse<Provincia[]>>('utility/getprovincias').then((res) => res.data),
  });
};

export const useMunicipiosQuery = () => {
  return useQuery<QueryResponse<Municipio[]>>({
    queryKey: ['municipios'],
    queryFn: () => api.get<QueryResponse<Municipio[]>>('utility/getmunicipios').then((res) => res.data),
  });
};

// ─── Colecciones del Programa InTEgrA ───────────────────────────────────────────

export interface CollectionItem {
  id: number;
  nombre: string;
  descripcion: string;
}

export interface RelacionParentesco {
  id: number;
  descripcion: string;
}

// ─── Profesionales de diagnóstico ───────────────────────────────────────────────

interface ProfesionalDiagnosticoResponse {
  idProfesionalesDiagnostico: number;
  nombre: string;
  descripcion: string | null;
}

export const useProfesionalesDiagnosticoQuery = () => {
  return useQuery<CollectionItem[]>({
    queryKey: ['profesionales-diagnostico'],
    queryFn: () =>
      apiDmars.get<ProfesionalDiagnosticoResponse[]>('/catalogos/profesionales-diagnostico').then((res) =>
        res.data.map((item) => ({
          id: item.idProfesionalesDiagnostico,
          nombre: item.nombre,
          descripcion: item.descripcion ?? '',
        }))
      ),
  });
};

// ─── Evaluaciones aplicadas ──────────────────────────────────────────────────────

interface EvaluacionAplicadaResponse {
  idEvaluacionAplicada: number;
  nombre: string;
  descripcion: string | null;
}

export const useEvaluacionesAplicadasQuery = () => {
  return useQuery<CollectionItem[]>({
    queryKey: ['evaluaciones-aplicadas'],
    queryFn: () =>
      apiDmars.get<EvaluacionAplicadaResponse[]>('/catalogos/evaluaciones-aplicadas').then((res) =>
        res.data.map((item) => ({
          id: item.idEvaluacionAplicada,
          nombre: item.nombre,
          descripcion: item.descripcion ?? '',
        }))
      ),
  });
};

// ─── Comorbilidades ──────────────────────────────────────────────────────────────

interface ComorbilidadResponse {
  idComorbilidad: number;
  nombre: string;
  descripcion: string | null;
}

export const useComorbilidadesQuery = () => {
  return useQuery<CollectionItem[]>({
    queryKey: ['comorbilidades'],
    queryFn: () =>
      apiDmars.get<ComorbilidadResponse[]>('/catalogos/comorbilidades').then((res) =>
        res.data.map((item) => ({
          id: item.idComorbilidad,
          nombre: item.nombre,
          descripcion: item.descripcion ?? '',
        }))
      ),
  });
};

// ─── Tipos de terapias ───────────────────────────────────────────────────────────

interface TipoTerapiaResponse {
  idTipoTerapia: number;
  nombre: string;
  descripcion: string | null;
}

export const useTerapiasQuery = () => {
  return useQuery<CollectionItem[]>({
    queryKey: ['terapias'],
    queryFn: () =>
      apiDmars.get<TipoTerapiaResponse[]>('/catalogos/tipos-de-terapias').then((res) =>
        res.data.map((item) => ({
          id: item.idTipoTerapia,
          nombre: item.nombre,
          descripcion: item.descripcion ?? '',
        }))
      ),
  });
};

// ─── Estado socio-económico ──────────────────────────────────────────────────────

interface EstadoSocialEconomicoResponse {
  idEstadoSocialEconomico: number;
  nombre: string;
  descripcion: string | null;
}

export const useCondicionesSocialesQuery = () => {
  return useQuery<CollectionItem[]>({
    queryKey: ['condiciones-sociales'],
    queryFn: () =>
      apiDmars.get<EstadoSocialEconomicoResponse[]>('/catalogos/estado-social-economico').then((res) =>
        res.data.map((item) => ({
          id: item.idEstadoSocialEconomico,
          nombre: item.nombre,
          descripcion: item.descripcion ?? '',
        }))
      ),
  });
};

// ─── Relación de parentescos ─────────────────────────────────────────────────────

export const useRelacionParentescosQuery = () => {
  return useQuery<RelacionParentesco[]>({
    queryKey: ['relacion-parentescos'],
    queryFn: () => apiDmars.get<RelacionParentesco[]>('/catalogos/relacion-parentescos').then((res) => res.data),
  });
};

// ─── Elegibilidad de Dependientes ───────────────────────────────────────────────

export interface DependienteElegible {
  idDependiente: number;
  nombreCompleto: string;
  edad: number;
  esElegible: boolean;
  restricciones: string[];
}

export const useDependientesElegiblesQuery = (idAfiliado: number | null) => {
  return useQuery<DependienteElegible[]>({
    queryKey: ['dependientes-elegibles', idAfiliado],
    queryFn: () =>
      apiDmars
        .get<DependienteElegible[]>(`/solicitudes/elegibilidad/dependientes/${idAfiliado}`)
        .then((res) => res.data),
    enabled: !!idAfiliado,
  });
};
